'use client'

import { useAtomValue } from 'jotai'
import { useMemo } from 'react'

import type { PnlTimeframe } from '@/features/dashboard/lib/account-summary-display'
import { type HomePortfolio, useHomePortfolio } from '@/features/home/hooks/use-home-portfolio'
import { type ActivityItem, buildActivityFeed } from '@/features/home/lib/home-activity'
import { assessPortfolioRisk, type PortfolioRisk } from '@/features/home/lib/home-risk'
import {
  accountDataLoadingAtom,
  allAlgoStatesAtom,
  allOpenOrdersAtom,
  allOrderHistoryAtom,
  allPositionsAtom,
  allTradesAtom,
} from '@/state/account'
import { activeResearchJobsAtom } from '@/state/research'
import { AlgoStatus, AlgoType } from '@/types/generated/algo'

// Finished algos (the account data manager keeps them for the Past sections); everything else is
// still working for the user.
const FINISHED_ALGO_STATUSES = new Set<AlgoStatus>([
  AlgoStatus.ALGO_STATUS_COMPLETED,
  AlgoStatus.ALGO_STATUS_CANCELLED,
  AlgoStatus.ALGO_STATUS_FAILED,
])

/** How many items the feed keeps — enough for "today" and "since last visit" counts. */
const FEED_DEPTH = 200

export interface HomeSnapshot {
  portfolio: HomePortfolio
  risk: PortfolioRisk
  /** Newest first, across every account. */
  activity: ActivityItem[]
  isActivityLoading: boolean
  /** Until the first positions snapshot lands, "no positions" can't be told from "not loaded". */
  isPositionsLoading: boolean
  openOrderCount: number
  /** The symbol of each open order (one entry per order). */
  openOrderSymbols: string[]
  /** Top-level algos still running, by kind. */
  automations: { hedges: number; arbs: number; total: number }
  researchRunning: number
}

/**
 * Everything Home summarizes, read once at the page and handed to its sections: the portfolio,
 * a risk read of each open position, the merged activity feed and what is running in the
 * background (algos, research). The atoms are kept fresh by the trade layout's syncs.
 */
export function useHomeSnapshot(timeframe: PnlTimeframe): HomeSnapshot {
  const portfolio = useHomePortfolio(timeframe)
  const positions = useAtomValue(allPositionsAtom)
  const trades = useAtomValue(allTradesAtom)
  const orderHistory = useAtomValue(allOrderHistoryAtom)
  const openOrders = useAtomValue(allOpenOrdersAtom)
  const algoStates = useAtomValue(allAlgoStatesAtom)
  const loading = useAtomValue(accountDataLoadingAtom)
  const researchJobs = useAtomValue(activeResearchJobsAtom)

  const risk = useMemo(() => assessPortfolioRisk(positions), [positions])
  const activity = useMemo(
    () => buildActivityFeed({ trades, orderHistory, openOrders }, FEED_DEPTH),
    [trades, orderHistory, openOrders]
  )
  const openOrderSymbols = useMemo(
    () => openOrders.flatMap((item) => (item.order ? [item.order.symbol] : [])),
    [openOrders]
  )
  const openOrderCount = openOrderSymbols.length
  const automations = useMemo(() => {
    const running = algoStates.filter(
      (state) => !state.parentId && !FINISHED_ALGO_STATUSES.has(state.status)
    )
    return {
      hedges: running.filter((state) => state.algoType === AlgoType.ALGO_TYPE_HEDGE).length,
      arbs: running.filter((state) => state.algoType === AlgoType.ALGO_TYPE_CROSS_EXCHANGE_ARB)
        .length,
      total: running.length,
    }
  }, [algoStates])

  return {
    portfolio,
    risk,
    activity,
    isActivityLoading: loading.orders || loading.trades,
    isPositionsLoading: loading.positions,
    openOrderCount,
    openOrderSymbols,
    automations,
    researchRunning: researchJobs.length,
  }
}
